import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';

interface CdnToken {
  policy: string;
  signature: string;
  keyPairId: string;
  expiresAt: number;
}

/**
 * Fetches a session-level CDN token and provides a URL-signing utility.
 *
 * Call in any page that references protected assets (`/releases/*`,
 * `/protected/*`, `/tubi/*`). The token is valid for 1 hour and
 * auto-refreshes 5 minutes before expiry.
 *
 * `signUrl(path)` appends Policy/Signature/Key-Pair-Id query params.
 * Returns the path unchanged if no token is available (Phase 1 / signing
 * not configured).
 */
export function useCdnToken() {
  const [token, setToken] = useState<CdnToken | null>(null);
  const [ready, setReady] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const fetchToken = useCallback(async () => {
    const authToken = useAuthStore.getState().token;
    if (!authToken) {
      setReady(true);
      return;
    }

    try {
      const resp = await fetch('/api/auth/cdn-token', {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (resp.status === 204) {
        // Phase 1: signing not configured, URLs work unsigned
        setReady(true);
        return;
      }

      if (resp.ok) {
        const data: CdnToken = await resp.json();
        setToken(data);
        setReady(true);

        // Schedule refresh 5 min before expiry
        const refreshIn = (data.expiresAt - Math.floor(Date.now() / 1000) - 300) * 1000;
        if (refreshIn > 0) {
          clearTimeout(refreshTimer.current);
          refreshTimer.current = setTimeout(fetchToken, refreshIn);
        }
      } else {
        // Non-OK response — proceed without token (Phase 1 fallback)
        setReady(true);
      }
    } catch {
      // Network error — proceed without token (Phase 1 fallback)
      setReady(true);
    }
  }, []);

  useEffect(() => {
    fetchToken();
    return () => clearTimeout(refreshTimer.current);
  }, [fetchToken]);

  const signUrl = useCallback(
    (path: string): string => {
      if (!token) return path;
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}Policy=${token.policy}&Signature=${token.signature}&Key-Pair-Id=${token.keyPairId}`;
    },
    [token],
  );

  return { signUrl, ready };
}
